/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/RecipientPickerDialog.tsx
 * FILE_ID: QUOTE-CLAIMS-COMP-001
 * PURPOSE: Recipient picker for sending a payment claim to the client.
 * WHY: Replaces the native window.prompt on the claim money path — searches
 *      the org's contacts (job's client first) with a free-text email fallback.
 * LANDMINE: /api/report/contact/ has no per-client server filter guarantee in
 *      all deployments; contacts are grouped client-side by client_name match.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText, Mail, Paperclip, Search, Send, User, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useApi } from '@/lib/setupApi'
import type { ClaimEvidence } from './evidence'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface ContactRow {
  id: number
  first_name: string
  last_name: string
  email: string
  position: string
  client: { client_name?: string } | null
}

interface Props {
  open: boolean
  clientName: string | null
  documentLabel: string
  documentNumber: string
  evidence: ClaimEvidence[]
  isSending: boolean
  onClose: () => void
  onSend: (request: DocumentSendRequest) => void
}

export interface DocumentSendRequest {
  to: string[]
  subject: string
  message: string
  format: 'pdf' | 'docx'
  evidence_ids: string[]
}

export default function RecipientPickerDialog({
  open,
  clientName,
  documentLabel,
  documentNumber,
  evidence,
  isSending,
  onClose,
  onSend,
}: Props) {
  const api = useApi()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [freeText, setFreeText] = useState('')
  const [freeTextError, setFreeTextError] = useState<string | null>(null)
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [format, setFormat] = useState<'pdf' | 'docx'>('pdf')
  const [selectedEvidence, setSelectedEvidence] = useState<string[]>([])
  const [confirming, setConfirming] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setSearch(''); setSelected([]); setFreeText(''); setFreeTextError(null)
      setSubject(`${documentLabel} ${documentNumber}`)
      setMessage(`Please find attached ${documentLabel.toLowerCase()} ${documentNumber}.`)
      setFormat('pdf')
      setSelectedEvidence(
        evidence.filter(item => item.include_on_send && !item.is_missing).map(item => item.id),
      )
      setConfirming(false)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [documentLabel, documentNumber, evidence, open])

  const { data: contacts = [], isLoading, isError } = useQuery<ContactRow[]>({
    queryKey: ['claim-recipient-contacts', search],
    queryFn: async () => {
      const r = await api.get('/api/report/contact/', { params: { search: search || undefined } })
      const rows = Array.isArray(r.data?.results) ? r.data.results : Array.isArray(r.data) ? r.data : []
      return rows.filter((c: ContactRow) => !!c.email)
    },
    enabled: open,
    staleTime: 30_000,
  })

  const { clientContacts, otherContacts } = useMemo(() => {
    const isClients = (c: ContactRow) =>
      !!clientName && (c.client?.client_name || '').toLowerCase() === clientName.toLowerCase()
    return {
      clientContacts: contacts.filter(isClients),
      otherContacts: contacts.filter(c => !isClients(c)),
    }
  }, [contacts, clientName])

  const toggle = (email: string) => {
    setConfirming(false)
    setSelected(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email])
  }

  const toggleEvidence = (id: string) => {
    setConfirming(false)
    setSelectedEvidence(prev => prev.includes(id)
      ? prev.filter(item => item !== id)
      : [...prev, id])
  }

  const addFreeText = () => {
    const email = freeText.trim()
    if (!email) return
    if (!EMAIL_RE.test(email)) { setFreeTextError('Enter a valid email address'); return }
    setConfirming(false)
    setFreeTextError(null)
    setFreeText('')
    if (!selected.includes(email)) setSelected(prev => [...prev, email])
  }

  if (!open) return null

  const selectedEvidenceBytes = evidence
    .filter(item => selectedEvidence.includes(item.id))
    .reduce((total, item) => total + (item.file_size ?? 0), 0)

  const renderContact = (c: ContactRow) => (
    <button
      key={c.id}
      type="button"
      onClick={() => toggle(c.email)}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors',
        selected.includes(c.email)
          ? 'bg-amber-50 dark:bg-amber-900/20 ring-1 ring-amber-400'
          : 'hover:bg-slate-50 dark:hover:bg-slate-700/50',
      )}
    >
      <User className="w-4 h-4 text-slate-400 shrink-0" />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-900 dark:text-white truncate">
          {`${c.first_name} ${c.last_name}`.trim() || c.email}
          {c.position && <span className="ml-2 text-xs font-normal text-slate-500">{c.position}</span>}
        </span>
        <span className="block text-xs text-slate-500 truncate">{c.email}</span>
      </span>
    </button>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={`Send ${documentLabel.toLowerCase()} to client`}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-2xl border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="font-semibold text-slate-900 dark:text-white">
            Send {documentLabel.toLowerCase()} to client
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-5 space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search contacts…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div className="max-h-52 overflow-y-auto space-y-1" aria-busy={isLoading}>
            {isLoading && <p className="text-sm text-slate-500 px-3 py-2">Loading contacts…</p>}
            {isError && <p className="text-sm text-red-600 px-3 py-2">Could not load contacts — use the email field below.</p>}
            {!isLoading && !isError && contacts.length === 0 && (
              <p className="text-sm text-slate-500 px-3 py-2">No contacts found — add an email below.</p>
            )}
            {clientContacts.length > 0 && (
              <>
                <p className="px-3 pt-1 text-xs font-semibold uppercase tracking-wider text-slate-400">{clientName}</p>
                {clientContacts.map(renderContact)}
              </>
            )}
            {otherContacts.length > 0 && (
              <>
                {clientContacts.length > 0 && <p className="px-3 pt-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Other contacts</p>}
                {otherContacts.map(renderContact)}
              </>
            )}
          </div>

          <div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={freeText}
                  onChange={e => { setFreeText(e.target.value); setFreeTextError(null) }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addFreeText() } }}
                  placeholder="Or type an email address…"
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <button type="button" onClick={addFreeText} className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300">Add</button>
            </div>
            {freeTextError && <p className="mt-1 text-xs text-red-600">{freeTextError}</p>}
          </div>

          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selected.map(email => (
                <span key={email} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-xs">
                  {email}
                  <button onClick={() => toggle(email)} aria-label={`Remove ${email}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Subject
              <input
                value={subject}
                onChange={e => { setSubject(e.target.value); setConfirming(false) }}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
              />
            </label>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Document
              <select
                value={format}
                onChange={e => {
                  setFormat(e.target.value as 'pdf' | 'docx')
                  setConfirming(false)
                }}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
              >
                <option value="pdf">PDF</option>
                <option value="docx">DOCX</option>
              </select>
            </label>
          </div>

          <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
            Message
            <textarea
              rows={4}
              value={message}
              onChange={e => { setMessage(e.target.value); setConfirming(false) }}
              className="mt-1 w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
            />
          </label>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <Paperclip className="h-3.5 w-3.5" />
                Evidence attachments
              </p>
              <span className="text-xs text-slate-400">
                {(selectedEvidenceBytes / (1024 * 1024)).toFixed(1)} MB selected
              </span>
            </div>
            {evidence.length === 0 ? (
              <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:bg-slate-900">
                No evidence is attached to this document.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {evidence.map(item => (
                  <li key={item.id}>
                    <label className={cn(
                      'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm',
                      item.is_missing
                        ? 'border-rose-200 bg-rose-50 text-rose-600'
                        : 'border-slate-200 dark:border-slate-700',
                    )}>
                      <input
                        type="checkbox"
                        checked={selectedEvidence.includes(item.id)}
                        disabled={item.is_missing}
                        onChange={() => toggleEvidence(item.id)}
                      />
                      <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="min-w-0 flex-1 truncate">
                        {item.caption || item.file_name || item.email_subject || 'Missing source'}
                      </span>
                      <span className="shrink-0 text-xs text-slate-400">
                        {item.is_missing
                          ? 'Unavailable'
                          : item.file_size
                            ? `${Math.ceil(item.file_size / 1024)} KB`
                            : item.source_type}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {confirming && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Confirm sending one {format.toUpperCase()} plus {selectedEvidence.length}{' '}
              evidence attachment{selectedEvidence.length === 1 ? '' : 's'} to{' '}
              {selected.length} recipient{selected.length === 1 ? '' : 's'}.
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700">Cancel</button>
          <button
            onClick={() => {
              if (!confirming) {
                setConfirming(true)
                return
              }
              onSend({
                to: selected,
                subject,
                message,
                format,
                evidence_ids: selectedEvidence,
              })
            }}
            disabled={selected.length === 0 || !subject.trim() || isSending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {isSending ? 'Sending…' : confirming ? 'Confirm send' : 'Review send'}
          </button>
        </div>
      </div>
    </div>
  )
}
